import { LinkedinBasicCredentials, LinkedinCookieCredentials } from '@unipile/provider-linkedin';
import { type ProviderAuthenticationIntentResponse } from '@unipile/types';
import {
  CancelButton,
  UniLoginForm,
  ScopesConsentScreen,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useHostedAuthSession,
  useScopesConsent,
  useStartProviderAuthIntent,
} from '@unipile/ui';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CookiesForm } from './CookiesForm';
import { CredentialsForm } from './CredentialsForm';

export function LoginScreen({
  onSuccess,
  onError,
  onCancel,
}: {
  onSuccess: (data: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const { data: session } = useHostedAuthSession();
  const allowMethods = session?.config?.linkedin?.allow_methods ?? ['credentials', 'unilogin'];
  const productsScope = session?.config?.linkedin?.products || ['classic', 'company', 'recruiter', 'sales_navigator'];
  const shouldCollectScopesBeforeAuthentication = session?.config?.linkedin?.allow_product_selection === false;
  const { scopes, hasScopes, brandName, cancelConsent } = useScopesConsent({ provider: 'linkedin' });
  const [pendingCredentials, setPendingCredentials] = useState<LinkedinBasicCredentials | LinkedinCookieCredentials>();

  const { mutate, isPending } = useStartProviderAuthIntent({
    mutationConfig: {
      onSuccess,
      onError: (error) => {
        if (error instanceof AxiosError) {
          const data = error.response?.data;
          if (data && typeof data === 'object' && 'type' in data && data.type === 'provider/invalid_credentials') {
            setPendingCredentials(undefined);
          }
        }
        onError(error);
      },
    },
  });

  const startAuthentication = (credentials: LinkedinBasicCredentials | LinkedinCookieCredentials) => {
    credentials.user_agent = navigator.userAgent;
    mutate({
      data: {
        provider: 'linkedin',
        credentials,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  };

  const onSubmit = (credentials: LinkedinBasicCredentials | LinkedinCookieCredentials) => {
    if (shouldCollectScopesBeforeAuthentication && hasScopes) {
      setPendingCredentials(credentials);
      return;
    }

    startAuthentication(credentials);
  };

  if (pendingCredentials) {
    return (
      <ScopesConsentScreen
        scopes={scopes}
        brandName={brandName}
        onAccept={() => startAuthentication(pendingCredentials)}
        onCancel={cancelConsent}
        isPending={isPending}
      />
    );
  }

  return (
    <>
      <Tabs
        defaultValue={
          allowMethods.includes('credentials') ? 'credentials' : allowMethods.includes('cookies') ? 'cookies' : 'unilogin'
        }
      >
        {allowMethods.length > 1 && (
          <TabsList className="w-full">
            {allowMethods.includes('credentials') && (
              <TabsTrigger className="w-full" value="credentials">
                {t('LoginScreen.method.credentials', { ns: 'linkedin' })}
              </TabsTrigger>
            )}

            {allowMethods.includes('cookies') && (
              <TabsTrigger className="w-full" value="cookies">
                {t('LoginScreen.method.cookies', { ns: 'linkedin' })}
              </TabsTrigger>
            )}
            {allowMethods.includes('unilogin') && (
              <TabsTrigger className="w-full" value="unilogin">
                {session?.unilogin_tab_name || 'UniLogin'}
              </TabsTrigger>
            )}
          </TabsList>
        )}
        {allowMethods.includes('unilogin') && (
          <TabsContent value="unilogin">
            <UniLoginForm provider="linkedin" onSuccess={onSuccess} onError={onError} onCancel={onCancel} />
          </TabsContent>
        )}
        <TabsContent value="credentials">
          <CredentialsForm onSubmit={onSubmit} isPending={isPending} />
          <CancelButton onCancel={onCancel} disabled={isPending} />
        </TabsContent>
        <TabsContent value="cookies">
          <CookiesForm onSubmit={onSubmit} isPending={isPending} products={productsScope} />
          <CancelButton onCancel={onCancel} disabled={isPending} />
        </TabsContent>
      </Tabs>
    </>
  );
}
